// Trekkin.app — App.jsx v7.5.0 (VIEWER LIVE FIX FINAL)
// ---------------------------------------------------------
// 🔹 Viewer (?share=XYZ) TOTALMENTE isolado
// 🔹 Viewer NÃO precisa de login
// 🔹 LIVE Share arranca automaticamente
// 🔹 Painel + guia aparecem sempre
// 🔹 Regra de Ouro: nada removido, só corrigido
// ---------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from "react";
import { App as CapacitorApp } from "@capacitor/app";

import {
  usePanelsStore,
  usePanelsLoginListener,
} from "./stores/usePanelsStore";

import usePanelsGPSBridge from "./stores/usePanelsGPSBridge.jsx";
import { useAuthStore, initAuthListener } from "./stores/useAuthStore";
import { useGroupStore } from "./stores/useGroupStore";
import { useShareStore } from "./stores/useShareStore";
import { useEventsStore } from "./stores/useEventsStore";
import usePushNotifications from "./hooks/usePushNotifications";
import useHistorySync from "./hooks/useHistorySync";
import useWaypointsSync from "./hooks/useWaypointsSync";
import { useBackgroundIdle } from "./hooks/useBackgroundIdle.js";

import MapView from "./components/MapView.jsx";
import AccommodationPartnerOverlay from "./components/AccommodationPartnerOverlay.jsx";
import PanelShell from "./components/PanelShell.jsx";
import { PANEL_COMPONENTS } from "./components/panelRegistry.jsx";
import TopControlBar from "./components/TopControlBar.jsx";
import FloatingButtonBar from "./components/FloatingButtonBar.jsx";
import PanelNav from "./components/PanelNav.jsx";
import PanelGuide from "./components/PanelGuide.jsx";
import PanelLogin from "./components/PanelLogin.jsx";
import IntroScreen from "./components/IntroScreen.jsx";
import PanelLiveShare from "./components/PanelLiveShare.jsx";
import PanelShellViewer from "./components/PanelShellViewer.jsx";
import LoadingDotsOverlay from "./components/LoadingDotsOverlay.jsx";
import LocationDisabledNotice from "./components/LocationDisabledNotice.jsx";

import { showToast } from "./components/NotifyToast.jsx";
import { auth, db } from "./firebaseConfig";
import { get, push, ref, update, serverTimestamp } from "firebase/database";
import { updateProfile } from "firebase/auth";
import {
  captureReferralTokensFromUrl,
  getPendingUserInviteToken,
  clearPendingUserInviteToken,
  getPendingEventInvite,
  clearPendingEventInvite,
  storeClaimedEventInvite,
  fetchReferralApi,
} from "./utils/referrals.js";
import { inferDistrictFromPostalPrefix } from "./utils/ptPostalDistrict.js";
import { startAlertsAutoSync } from "./utils/alertsSync.js";
import { clearNativeAppBadge } from "./functions/pushNotifications.js";
import { parseGPX } from "./functions/parseGPX.js";
import { getPublicTrailRuntimeId } from "./utils/publicTrailsCatalog.js";

export default function App() {
  // -----------------------------------------------------
  // STORES
  // -----------------------------------------------------
  const { currentPanel, isFullscreen, openPanel } =
    usePanelsStore();
  const recordingRecoveredNoticePending = usePanelsStore(
    (s) => s.recordingRecoveredNoticePending
  );
  const isRecording = usePanelsStore((s) => s.isRecording);
  const recoverRecordingFromNativeSnapshot = usePanelsStore(
    (s) => s.recoverRecordingFromNativeSnapshot
  );
  const consumeRecordingRecoveredNotice = usePanelsStore(
    (s) => s.consumeRecordingRecoveredNotice
  );
  const { user, authReady } = useAuthStore();
  const { loadGroupsOnce, myGroups, joinGroup, currentGroup, listenGroups, stopListeningGroups } =
    useGroupStore();
  const syncShareState = useShareStore((s) => s.syncShareState);
  const isSharing = useShareStore((s) => s.isSharing);
  const stopShare = useShareStore((s) => s.stopShare);
  const backgroundIdle = useBackgroundIdle();
  const pauseEventIdleListeners = useEventsStore((s) => s.pauseBackgroundIdleListeners);
  const resumeEventIdleListeners = useEventsStore((s) => s.resumeBackgroundIdleListeners);
  const [processingOverlay, setProcessingOverlay] = useState(null);
  const [initialLoaderVisible, setInitialLoaderVisible] = useState(true);
  const publicTrailDeepLinkHandledRef = useRef("");
  const processingOverlayRef = useRef(null);
  const processingHideTimerRef = useRef(null);

  // -----------------------------------------------------
  // GPS Bridge (sempre ativo)
  // -----------------------------------------------------
  usePanelsGPSBridge();
  usePushNotifications();
  useHistorySync();
  useWaypointsSync();

  useEffect(() => {
    startAlertsAutoSync();
  }, []);

  useEffect(() => {
    const receiveSharedGpx = (event) => {
      const payload = event?.detail || window.__trekkinSharedGpx;
      if (!payload?.text || !payload?.name) return;
      window.__trekkinSharedGpx = payload;
      if (usePanelsStore.getState().currentPanel?.name !== "nav") {
        usePanelsStore.getState().openPanel("nav");
      }
      window.dispatchEvent(new Event("trekkin:sharedGpxReady"));
    };
    window.addEventListener("native:sharedGpx", receiveSharedGpx);
    receiveSharedGpx();
    return () => window.removeEventListener("native:sharedGpx", receiveSharedGpx);
  }, []);

  useEffect(() => {
    const handleProcessing = (event) => {
      const detail = event?.detail || {};
      const id = String(detail.id || "global-processing");

      if (detail.active !== false) {
        if (processingHideTimerRef.current) {
          window.clearTimeout(processingHideTimerRef.current);
          processingHideTimerRef.current = null;
        }
        const next = {
          id,
          text: String(detail.text || "A processar..."),
          startedAt: Date.now(),
          minimumMs: Math.max(300, Number(detail.minimumMs) || 650),
        };
        processingOverlayRef.current = next;
        setProcessingOverlay(next);
        return;
      }

      const current = processingOverlayRef.current;
      if (!current || current.id !== id) return;
      const elapsed = Date.now() - current.startedAt;
      const remaining = Math.max(0, current.minimumMs - elapsed);
      const hide = () => {
        if (processingOverlayRef.current?.id !== id) return;
        processingOverlayRef.current = null;
        processingHideTimerRef.current = null;
        setProcessingOverlay(null);
      };
      if (remaining > 0) {
        processingHideTimerRef.current = window.setTimeout(hide, remaining);
      } else {
        hide();
      }
    };

    window.addEventListener("trekkin:processing", handleProcessing);
    return () => {
      window.removeEventListener("trekkin:processing", handleProcessing);
      if (processingHideTimerRef.current) {
        window.clearTimeout(processingHideTimerRef.current);
      }
    };
  }, []);

  // -----------------------------------------------------
  // VIEWER MODE (?share=XYZ) — ISOLADO
  // -----------------------------------------------------
  const urlParams = new URLSearchParams(window.location.search);
  const shareId = urlParams.get("share");
  const isShareMode = !!shareId;
  const isIosSafeMode = (() => {
    try {
      return (
        window?.__TREKKIN_IOS_SAFE_MODE__ === true ||
        window?.localStorage?.getItem("trekkin.ios.safeMode") === "1"
      );
    } catch {
      return false;
    }
  })();

  const [viewerMinimized, setViewerMinimized] = useState(false);
  const [showPostalPrompt, setShowPostalPrompt] = useState(false);
  const [showSocialProfilePrompt, setShowSocialProfilePrompt] = useState(false);
  const [socialProfileName, setSocialProfileName] = useState("");
  const [socialProfileSaving, setSocialProfileSaving] = useState(false);
  const [socialProfileError, setSocialProfileError] = useState("");
  const socialProfileCheckedUidRef = useRef(null);
  const [postalCode, setPostalCode] = useState("");
  const [postalPromptSaving, setPostalPromptSaving] = useState(false);
  const [postalPromptError, setPostalPromptError] = useState("");
  const postalPromptCheckedUidRef = useRef(null);
  const eventDeepLinkHandledRef = useRef(false);
  const referralTokensCapturedRef = useRef(false);
  const screenshotAlertLastTsRef = useRef(0);
  const screenWakeLockRef = useRef(null);

  const normalizePostalCode = (raw) => {
    const digits = String(raw || "").replace(/\D/g, "");
    if (digits.length !== 7) return "";
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  };

  const isPostalCodeValidPt = (raw) => /^\d{4}-\d{3}$/.test(String(raw || ""));

  const normalizePersonName = (raw) =>
    String(raw || "").trim().replace(/\s+/g, " ").slice(0, 100);

  useEffect(() => {
    if (!authReady || !user?.uid || isShareMode) return;
    if (socialProfileCheckedUidRef.current === user.uid) return;
    let cancelled = false;

    (async () => {
      try {
        const userSnap = await get(ref(db, `users/${user.uid}`));
        if (cancelled) return;
        const dbUser = userSnap.exists() ? userSnap.val() || {} : {};
        const providerIds = (auth.currentUser?.providerData || [])
          .map((item) => String(item?.providerId || "").toLowerCase())
          .filter(Boolean);
        const storedProvider = String(dbUser?.authProvider || user?.authProvider || "").toLowerCase();
        if (providerIds.length === 0 && !storedProvider) return;
        socialProfileCheckedUidRef.current = user.uid;
        const isSocialAccount =
          providerIds.includes("google.com") || providerIds.includes("apple.com") ||
          /google|apple/.test(storedProvider);
        if (!isSocialAccount || dbUser?.profileNameCompletedAt) {
          setShowSocialProfilePrompt(false);
          return;
        }

        const currentName = normalizePersonName(dbUser?.name || user?.name || "");
        const emailLocal = String(user?.email || dbUser?.email || "")
          .split("@")[0].trim().toLowerCase();
        const normalizedCurrent = currentName.toLowerCase();
        const hasUsefulName = currentName.length >= 2 &&
          normalizedCurrent !== "trekker" && normalizedCurrent !== "utilizador" &&
          normalizedCurrent !== emailLocal;
        if (hasUsefulName) {
          setShowSocialProfilePrompt(false);
          return;
        }

        setSocialProfileName("");
        setSocialProfileError("");
        setShowSocialProfilePrompt(true);
      } catch {
        if (!cancelled) setShowSocialProfilePrompt(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authReady, isShareMode, user?.authProvider, user?.email, user?.name, user?.uid]);

  const saveSocialProfileName = async (event) => {
    event?.preventDefault?.();
    if (!user?.uid || socialProfileSaving) return;
    const fullName = normalizePersonName(socialProfileName);
    if (fullName.length < 2 || !/[A-Za-zÀ-ÖØ-öø-ÿ]/.test(fullName)) {
      setSocialProfileError("Indica o teu nome para continuar.");
      return;
    }

    setSocialProfileSaving(true);
    setSocialProfileError("");
    try {
      const parts = fullName.split(" ");
      const firstName = parts[0] || fullName;
      const lastName = parts.slice(1).join(" ") || null;
      if (auth.currentUser) await updateProfile(auth.currentUser, { displayName: fullName });
      await update(ref(db), {
        [`users/${user.uid}/name`]: fullName,
        [`users/${user.uid}/firstName`]: firstName,
        [`users/${user.uid}/lastName`]: lastName,
        [`users/${user.uid}/profileNameCompletedAt`]: serverTimestamp(),
        [`usersPublic/${user.uid}/name`]: fullName,
        [`usersPublic/${user.uid}/updatedAt`]: serverTimestamp(),
      });
      const updatedUser = { ...user, name: fullName, displayName: fullName, firstName, lastName, profileNameCompletedAt: Date.now() };
      useAuthStore.setState({ user: updatedUser });
      try {
        localStorage.setItem("trekkin_session", JSON.stringify({ user: updatedUser, ts: Date.now() }));
      } catch {}
      setShowSocialProfilePrompt(false);
      showToast("Perfil atualizado. Bem-vindo à Trekkin!", "success");
    } catch (error) {
      console.warn("Falha ao completar perfil social:", error);
      setSocialProfileError("Não foi possível guardar agora. Tenta novamente.");
    } finally {
      setSocialProfileSaving(false);
    }
  };

  // -----------------------------------------------------
  // 👀 VIEWER BOOTSTRAP (LIVE SHARE)
  // -----------------------------------------------------
  const listenShare = useShareStore((s) => s.listenShare);

  useEffect(() => {
    if (!isShareMode || !shareId) return;

    console.log("👀 Viewer bootstrap → listenShare:", shareId);

    listenShare(shareId);

    openPanel("liveShare", {
      shareId,
      viewer: true,
    });
  }, [isShareMode, shareId, listenShare, openPanel]);

  // -----------------------------------------------------
  // 🔗 DEEPLINK EVENTO WEB (?eventId=...)
  // -----------------------------------------------------
  useEffect(() => {
    if (referralTokensCapturedRef.current) return;
    referralTokensCapturedRef.current = true;
    captureReferralTokensFromUrl();
  }, []);

  useEffect(() => {
    if (!authReady || !user?.uid) return;

    const COOLDOWN_MS = 15_000;
    let nativeSub = null;

    const notifyScreenshot = async (source = "unknown") => {
      const now = Date.now();
      if (now - screenshotAlertLastTsRef.current < COOLDOWN_MS) return;
      screenshotAlertLastTsRef.current = now;

      try {
        const payload = {
          type: "screenshot",
          sub: "captura_ecra",
          detail: "Captura de ecrã detetada",
          label: "Captura de ecrã detetada",
          adminOnly: true,
          userId: user.uid,
          uid: user.uid,
          userName: user.name || user.displayName || user.email || "Utilizador",
          name: user.name || user.displayName || user.email || "Utilizador",
          email: user.email || null,
          source,
          platform: String(window?.Capacitor?.getPlatform?.() || "web"),
          ts: now,
          persistent: true,
        };
        await push(ref(db, "alerts/seguranca"), payload);
      } catch (err) {
        console.warn("⚠️ Falha ao gravar alerta de screenshot:", err);
      }
    };

    const onKeyDown = (ev) => {
      const key = String(ev?.key || "").toLowerCase();
      if (key === "printscreen" || key === "prtsc") {
        notifyScreenshot("web-printscreen");
      }
    };
    const onNativeScreenshot = () => notifyScreenshot("native-ios");

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("native:screenshot", onNativeScreenshot);

    try {
      const plugin =
        window?.Capacitor?.Plugins?.ScreenGuard ||
        window?.Capacitor?.Plugins?.GPSNativePlugin ||
        window?.Capacitor?.Plugins?.GPSNative;
      if (plugin?.addListener) {
        nativeSub = plugin.addListener("screenshotTaken", () => {
          notifyScreenshot("native-screenshot");
        });
      }
    } catch {}

    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("native:screenshot", onNativeScreenshot);
      try {
        nativeSub?.remove?.();
      } catch {}
    };
  }, [authReady, user?.uid, user?.name, user?.displayName, user?.email]);

  useEffect(() => {
    const capacitor = window?.Capacitor;
    const isNative = !!capacitor?.isNativePlatform?.();
    const platform = String(capacitor?.getPlatform?.() || "").toLowerCase();

    if (!isNative || (platform !== "android" && platform !== "ios")) return;

    let disposed = false;
    let appStateSub = null;

    const getNativeKeepAwakePlugin = () => {
      const plugins = window?.Capacitor?.Plugins || {};
      return (
        plugins.KeepAwakeNative ||
        plugins.KeepAwakeNativePlugin ||
        plugins.GPSNative ||
        plugins.GPSNativePlugin ||
        null
      );
    };

    const setNativeKeepAwake = async (enabled) => {
      try {
        const plugin = getNativeKeepAwakePlugin();
        if (typeof plugin?.setKeepScreenAwake === "function") {
          await plugin.setKeepScreenAwake({ enabled: !!enabled });
        }
      } catch {}
    };

    const releaseWebWakeLock = async () => {
      const lock = screenWakeLockRef.current;
      screenWakeLockRef.current = null;
      try {
        await lock?.release?.();
      } catch {}
    };

    const requestWebWakeLock = async () => {
      if (screenWakeLockRef.current) return;
      if (document.visibilityState !== "visible") return;
      try {
        const wakeLock = await navigator?.wakeLock?.request?.("screen");
        if (!wakeLock) return;
        screenWakeLockRef.current = wakeLock;
        wakeLock.addEventListener?.("release", () => {
          if (screenWakeLockRef.current === wakeLock) {
            screenWakeLockRef.current = null;
          }
        });
      } catch {}
    };

    const setKeepAwake = async (enabled) => {
      await setNativeKeepAwake(enabled);
      if (enabled) {
        await requestWebWakeLock();
      } else {
        await releaseWebWakeLock();
      }
    };

    const applyCurrentVisibility = () => {
      if (disposed) return;
      const isVisible = document.visibilityState === "visible";
      setKeepAwake(isVisible);
      if (isVisible) clearNativeAppBadge();
    };

    const onVisibilityChange = () => {
      applyCurrentVisibility();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (disposed) return;
      setKeepAwake(isActive === true);
      if (isActive === true) clearNativeAppBadge();
    })
      .then((sub) => {
        appStateSub = sub;
      })
      .catch(() => {});

    applyCurrentVisibility();

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      try {
        appStateSub?.remove?.();
      } catch {}
      setNativeKeepAwake(false);
      releaseWebWakeLock();
    };
  }, []);

  useEffect(() => {
    if (isShareMode) return;
    if (eventDeepLinkHandledRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const eventIdParam =
      params.get("eventId") ||
      params.get("event") ||
      params.get("evt") ||
      null;

    if (!eventIdParam || !authReady || !user?.uid) return;

    eventDeepLinkHandledRef.current = true;

    const eventId = String(eventIdParam).trim();
    if (!eventId) return;

    const timer = setTimeout(() => {
      openPanel("eventDetails", { eventId });
      try {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete("eventId");
        cleanUrl.searchParams.delete("event");
        cleanUrl.searchParams.delete("evt");
        window.history.replaceState({}, "", cleanUrl.toString());
      } catch {}
    }, 120);

    return () => clearTimeout(timer);
  }, [authReady, user?.uid, isShareMode, openPanel]);

  useEffect(() => {
    if (isShareMode) return undefined;
    let disposed = false;
    let appUrlSub = null;

    const openPublicTrail = async (inputUrl) => {
      try {
        const url = new URL(String(inputUrl || window.location.href), window.location.href);
        const filename = String(
          url.searchParams.get("publicTrail") ||
          url.searchParams.get("trail") ||
          (url.protocol === "trekkin:" && url.hostname === "trail" ? url.pathname.replace(/^\/+/, "") : "")
        ).trim();
        if (!filename || publicTrailDeepLinkHandledRef.current === filename) return;
        publicTrailDeepLinkHandledRef.current = filename;
        const listResponse = await fetch("https://www.trekkin.pt/app/app-upload/list-public-gpx.php");
        if (!listResponse.ok) throw new Error(`HTTP ${listResponse.status}`);
        const list = await listResponse.json();
        const item = (Array.isArray(list?.items) ? list.items : []).find(
          (candidate) => String(candidate?.filename || "") === filename
        );
        if (!item) throw new Error("Trilho público não encontrado");
        const rawUrl = String(item.url || "");
        const absoluteUrl = rawUrl.startsWith("http") ? rawUrl : `https://www.trekkin.pt${rawUrl}`;
        const gpxResponse = await fetch(absoluteUrl);
        if (!gpxResponse.ok) throw new Error(`HTTP ${gpxResponse.status}`);
        const parsed = await parseGPX(await gpxResponse.text());
        const points = Array.isArray(parsed?.points) ? parsed.points : [];
        if (points.length < 2) throw new Error("Trilho sem pontos suficientes");
        const trail = {
          id: getPublicTrailRuntimeId(item, absoluteUrl),
          nome: item.name || item.filename,
          name: item.name || item.filename,
          shortDescription: String(item.shortDescription || ""),
          galleryImages: Array.isArray(item.gallery) ? item.gallery : [],
          points,
          cloud: { file: item.filename, url: rawUrl },
          __source: "public-gpx",
          public: true,
        };
        if (disposed) return;
        const panelsState = usePanelsStore.getState();
        panelsState.setTrailData(trail);
        panelsState.openPanel("trailDetails", { trail });
        try {
          if (url.protocol === "http:" || url.protocol === "https:") {
            const cleanUrl = new URL(window.location.href);
            cleanUrl.searchParams.delete("publicTrail");
            cleanUrl.searchParams.delete("trail");
            window.history.replaceState({}, "", cleanUrl.toString());
          }
        } catch {}
      } catch (error) {
        publicTrailDeepLinkHandledRef.current = "";
        console.warn("Falha ao abrir deep link de trilho público:", error);
        showToast("Não foi possível abrir este trilho.", "error");
      }
    };

    openPublicTrail(window.location.href);
    const onNativeDeepLink = (event) => openPublicTrail(event?.detail?.url || event?.detail);
    window.addEventListener("native:deepLink", onNativeDeepLink);
    CapacitorApp.addListener("appUrlOpen", ({ url }) => openPublicTrail(url))
      .then((subscription) => { appUrlSub = subscription; })
      .catch(() => {});

    return () => {
      disposed = true;
      window.removeEventListener("native:deepLink", onNativeDeepLink);
      try { appUrlSub?.remove?.(); } catch {}
    };
  }, [isShareMode]);

  useEffect(() => {
    if (!authReady || !user?.uid || !auth.currentUser) return;
    let cancelled = false;

    const syncReferrals = async () => {
      try {
        const idToken = await auth.currentUser.getIdToken(true);

        const userInviteToken = getPendingUserInviteToken();
        if (userInviteToken) {
          try {
            await fetchReferralApi("/referrals/claim-user-invite", {
              token: idToken,
              body: { token: userInviteToken },
            });
            if (!cancelled) clearPendingUserInviteToken();
          } catch (e) {
            const msg = String(e?.message || "");
            if (!cancelled) {
              if (msg === "invite_email_mismatch") {
                showToast("Este convite não pertence a este email.", "warning");
              }
              if (
                msg === "invite_email_mismatch" ||
                msg === "invite_already_claimed" ||
                msg === "user_already_referred" ||
                msg === "invite_self_claim" ||
                msg === "invite_not_found"
              ) {
                clearPendingUserInviteToken();
              }
            }
          }
        }

        const pendingEventInvite = getPendingEventInvite();
        if (pendingEventInvite?.token) {
          try {
            const result = await fetchReferralApi("/referrals/claim-event-invite", {
              token: idToken,
              body: { token: pendingEventInvite.token },
            });
            if (!cancelled) {
              storeClaimedEventInvite(result);
              clearPendingEventInvite();
            }
          } catch (e) {
            const msg = String(e?.message || "");
            if (!cancelled) {
              if (msg === "event_invite_email_mismatch") {
                showToast("Este convite de evento não pertence a este email.", "warning");
              }
              if (
                msg === "event_invite_email_mismatch" ||
                msg === "event_invite_already_claimed" ||
                msg === "event_invite_self_claim" ||
                msg === "event_invite_not_found" ||
                msg === "event_already_registered"
              ) {
                clearPendingEventInvite();
              }
            }
          }
        }

        const isNative = !!window?.Capacitor?.isNativePlatform?.();
        const platform = String(window?.Capacitor?.getPlatform?.() || "").toLowerCase();
        const normalizedPlatform =
          platform === "android" || platform === "ios" ? platform : "web";

        await fetchReferralApi("/referrals/auto-link-user-invite-by-email", {
          token: idToken,
          body: { platform: normalizedPlatform },
        }).catch(() => null);

        if (isNative && (platform === "android" || platform === "ios")) {
          await fetchReferralApi("/referrals/activate-native-user-invite", {
            token: idToken,
            body: { platform },
          }).catch(() => null);
        }
      } catch {}
    };

    syncReferrals();
    return () => {
      cancelled = true;
    };
  }, [authReady, user?.uid]);

  // 👉 VIEWER SAI AQUI (não passa por auth/login)
  if (isShareMode) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          overflow: "hidden",
          background: "#e9f1eb",
        }}
      >
        <MapView isShareMode />

        {currentPanel?.name === "liveShare" && (
          <PanelShellViewer isMinimized={viewerMinimized}>
            <PanelLiveShare
              {...currentPanel.props}
              setIsMinimizedExternal={setViewerMinimized}
            />
          </PanelShellViewer>
        )}
      </div>
    );
  }

  // -----------------------------------------------------
  // APP NORMAL
  // -----------------------------------------------------
  usePanelsLoginListener();

  useEffect(() => {
    initAuthListener();
  }, []);

  // -----------------------------------------------------
  // LIVE SHARE SYNC (1 ativo por user)
  // -----------------------------------------------------
  useEffect(() => {
    if (!user?.uid) return;
    syncShareState({ reason: "boot" });
  }, [user?.uid, syncShareState]);

  useEffect(() => {
    if (!user?.uid) return;
    if (isRecording) return;
    if (!isSharing) return;

    let cancelled = false;
    (async () => {
      try {
        await stopShare();
        if (!cancelled) {
          showToast("Live Share terminado automaticamente ao parar a gravação.", "info");
        }
      } catch {}
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid, isRecording, isSharing, stopShare]);

  // -----------------------------------------------------
  // GROUPS SNAPSHOT APÓS LOGIN
  // -----------------------------------------------------
  useEffect(() => {
    if (!user) return;
    if (typeof listenGroups === "function") return;
    loadGroupsOnce();
  }, [user?.uid, loadGroupsOnce, listenGroups]);

  useEffect(() => {
    if (!user?.uid) return;
    if (backgroundIdle) {
      pauseEventIdleListeners?.();
    } else {
      resumeEventIdleListeners?.();
    }
  }, [user?.uid, backgroundIdle, pauseEventIdleListeners, resumeEventIdleListeners]);

  useEffect(() => {
    if (!user?.uid) return;
    if (typeof listenGroups !== "function") return;
    listenGroups();
    return () => {
      if (typeof stopListeningGroups === "function") {
        stopListeningGroups();
      }
    };
  }, [user?.uid, listenGroups, stopListeningGroups]);

  useEffect(() => {
    if (!authReady || !user?.uid || isShareMode) return;
    if (postalPromptCheckedUidRef.current === user.uid) return;
    postalPromptCheckedUidRef.current = user.uid;

    let cancelled = false;

    (async () => {
      try {
        const userSnap = await get(ref(db, `users/${user.uid}`));
        if (cancelled) return;

        const dbUser = userSnap.exists() ? userSnap.val() || {} : {};
        const choice = String(
          dbUser?.postalPromptChoice || user?.postalPromptChoice || ""
        )
          .trim()
          .toLowerCase();
        const hasPostalCode = !!String(
          dbUser?.postalCode || user?.postalCode || ""
        ).trim();

        // Se já forneceu código postal, não voltar a pedir.
        if (choice === "provided" || hasPostalCode) {
          setShowPostalPrompt(false);
          return;
        }

        // "Agora não" e "Não pretendo responder" => voltar a pedir em 30 dias.
        const nextPromptAtRaw =
          dbUser?.postalPromptNextPromptAt ??
          user?.postalPromptNextPromptAt ??
          null;
        const nextPromptAt =
          typeof nextPromptAtRaw === "number" ? nextPromptAtRaw : Number(nextPromptAtRaw || 0);

        // Compatibilidade com dados antigos que só tinham completedAt:
        // se não forneceu CP, treat completedAt como "adiado 30 dias".
        const completedAtRaw =
          dbUser?.postalPromptCompletedAt ??
          user?.postalPromptCompletedAt ??
          dbUser?.demographics?.postalPromptCompletedAt ??
          user?.demographics?.postalPromptCompletedAt ??
          null;
        const completedAt =
          typeof completedAtRaw === "number" ? completedAtRaw : Number(completedAtRaw || 0);
        const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
        const fallbackNextFromCompleted = completedAt > 0 ? completedAt + THIRTY_DAYS_MS : 0;
        const effectiveNextPromptAt = nextPromptAt > 0 ? nextPromptAt : fallbackNextFromCompleted;

        if (effectiveNextPromptAt > Date.now()) {
          setShowPostalPrompt(false);
          return;
        }

        setPostalCode("");
        setPostalPromptError("");
        setShowPostalPrompt(true);
      } catch {
        if (!cancelled) setShowPostalPrompt(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    authReady,
    user?.uid,
    user?.postalPromptCompletedAt,
    user?.postalPromptChoice,
    user?.postalPromptNextPromptAt,
    user?.postalCode,
    isShareMode,
  ]);

  const savePostalPrompt = async (choice) => {
    if (!user?.uid || postalPromptSaving) return;

    const normalized = normalizePostalCode(postalCode);
    const wantsToProvide = choice === "provided";

    if (wantsToProvide && !isPostalCodeValidPt(normalized)) {
      setPostalPromptError("Indica um código postal válido (ex: 4700-000).");
      return;
    }

    setPostalPromptSaving(true);
    setPostalPromptError("");
    try {
      const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
      const payload = {
        postalPromptChoice: choice,
      };

      if (wantsToProvide) {
        payload.postalPromptCompletedAt = serverTimestamp();
        payload.postalPromptNextPromptAt = null;
        payload.postalCode = normalized;
        payload.postalCodePrefix = normalized.slice(0, 4);
        payload.postalCodeProvidedAt = serverTimestamp();
        const inferredDistrict = inferDistrictFromPostalPrefix(normalized);
        if (inferredDistrict) {
          payload.district = inferredDistrict;
          payload.districtSource = "postal_prefix_inferred";
        }
      } else {
        payload.postalPromptNextPromptAt = Date.now() + THIRTY_DAYS_MS;
      }

      await update(ref(db, `users/${user.uid}`), payload);
      setShowPostalPrompt(false);
      if (wantsToProvide) showToast("Código postal guardado. Obrigado!", "success");
    } catch {
      setPostalPromptError("Não foi possível guardar agora. Tenta novamente.");
    } finally {
      setPostalPromptSaving(false);
    }
  };

  // -----------------------------------------------------
  // AUTO-JOIN GRUPO
  // -----------------------------------------------------
  useEffect(() => {
    if (!user?.uid) return;

    const currentId =
      typeof currentGroup === "string"
        ? currentGroup
        : currentGroup?.id ||
          currentGroup?.groupId ||
          currentGroup?.firebaseId ||
          currentGroup?.key ||
          null;

    const statusIsActive = (raw) => {
      const status = String(raw || "")
        .trim()
        .toLowerCase();
      if (!status) return false;
      return ![
        "cancelled",
        "canceled",
        "declined",
        "rejected",
        "removed",
        "expired",
      ].includes(status);
    };

    const resolveConfirmedGroup = (groupsArr = [], userEventsMap = {}) => {
      if (!Array.isArray(groupsArr) || !groupsArr.length) return null;

      const candidate = groupsArr.find((g) => {
        const evId = g?.activeEventId || g?.activeEvent || g?.eventId || null;
        if (!evId) return false;
        const ue = userEventsMap?.[evId] || null;
        return statusIsActive(ue?.status || ue?.state || ue?.registrationStatus);
      });

      return candidate || groupsArr.find((g) => g?.activeEventId) || groupsArr[0] || null;
    };

    const currentGroupBelongsToUser =
      !!currentId &&
      Array.isArray(myGroups) &&
      myGroups.some(
        (g) =>
          g?.id === currentId ||
          g?.groupId === currentId ||
          g?.firebaseId === currentId ||
          g?.key === currentId
      );

    if (currentGroupBelongsToUser) return;

    let cancelled = false;

    const normalizeMembers = (members) => {
      if (!members || typeof members !== "object") return {};
      return Object.fromEntries(
        Object.entries(members).filter(([, val]) => val !== null && val !== false)
      );
    };

    const run = async () => {
      let userEventsMap = {};
      try {
        const ueSnap = await get(ref(db, `/userEvents/${user.uid}`));
        userEventsMap = ueSnap.exists() ? ueSnap.val() || {} : {};
      } catch {}

      const localGroups = Array.isArray(myGroups) ? myGroups : [];
      const localTarget = resolveConfirmedGroup(localGroups, userEventsMap);
      const localGid = localTarget?.id || localTarget?.groupId || null;
      if (localGid) {
        if (!cancelled) joinGroup(localGid);
        return;
      }

      // Fallback: ler /groups diretamente do RTDB para não depender de cache local.
      try {
        const snap = await get(ref(db, "/groups"));
        if (!snap.exists() || cancelled) return;

        const raw = snap.val() || {};
        const arr = Object.entries(raw).map(([id, v]) => ({
          id,
          ...(v || {}),
          members: normalizeMembers(v?.members),
        }));

        const mine = arr.filter((g) => !!g?.members?.[user.uid]);
        const target = resolveConfirmedGroup(mine, userEventsMap);
        const gid = target?.id || target?.groupId || null;
        if (!cancelled && gid) {
          joinGroup(gid);
        }
      } catch {}
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [user?.uid, myGroups, currentGroup, joinGroup]);

  // -----------------------------------------------------
  // Toast da build
  // -----------------------------------------------------
  useEffect(() => {
    const label = window.__TREKKIN_BUILD_INFO?.label;
    if (label) showToast(label, "info");
  }, []);

  useEffect(() => {
    let cancelled = false;

    const sleep = (ms) =>
      new Promise((resolve) => {
        window.setTimeout(resolve, ms);
      });

    const runRecovery = async () => {
      if (typeof recoverRecordingFromNativeSnapshot !== "function") return;

      const isNative = !!window?.Capacitor?.isNativePlatform?.();
      const platform = window?.Capacitor?.getPlatform?.() || "";

      if (!isNative || platform !== "android") {
        await recoverRecordingFromNativeSnapshot();
        return;
      }

      // Capacitor plugins are already available when App mounts. Recover at
      // once so a recreated Android WebView does not add another visible wait.
      // Retry times are absolute; the previous cumulative sleeps could delay
      // the final attempt by more than 12 seconds.
      const retryAtMs = [0, 350, 1200, 2800];
      const recoveryStartedAt = Date.now();
      for (let i = 0; i < retryAtMs.length; i += 1) {
        if (cancelled) return;
        const remainingMs = retryAtMs[i] - (Date.now() - recoveryStartedAt);
        if (remainingMs > 0) await sleep(remainingMs);
        if (cancelled) return;

        const result = await recoverRecordingFromNativeSnapshot();
        if (result?.ok) return;
        if (result?.reason === "not_native" || result?.reason === "not_android") {
          return;
        }
      }
    };

    runRecovery();
    return () => {
      cancelled = true;
    };
  }, [recoverRecordingFromNativeSnapshot]);

  useEffect(() => {
    if (!recordingRecoveredNoticePending) return;
    showToast("Sessão de gravação recuperada após falha.", "warning", 4200);
    consumeRecordingRecoveredNotice?.();
  }, [recordingRecoveredNoticePending, consumeRecordingRecoveredNotice]);

  // -----------------------------------------------------
  // Resolver painel ativo
  // -----------------------------------------------------
  const { ResolvedPanel, resolvedProps } = useMemo(() => {
    if (!currentPanel?.name)
      return { ResolvedPanel: null, resolvedProps: {} };

    const name = currentPanel.name;

    if (name === "intro") return { ResolvedPanel: null, resolvedProps: {} };
    if (name === "login")
      return { ResolvedPanel: PanelLogin, resolvedProps: currentPanel.props };
    if (name === "guide")
      return { ResolvedPanel: PanelGuide, resolvedProps: currentPanel.props };
    if (name === "liveShare")
      return { ResolvedPanel: PanelLiveShare, resolvedProps: currentPanel.props };

    const Comp = PANEL_COMPONENTS[name] || PanelNav;
    return { ResolvedPanel: Comp, resolvedProps: currentPanel.props };
  }, [currentPanel]);

  const isCoverPanel = ["intro", "login"].includes(currentPanel?.name);

  // -----------------------------------------------------
  // RENDER FINAL
  // -----------------------------------------------------
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        background: "#e9f1eb",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      {/* MAPA */}
      <div style={{ position: "absolute", inset: 0, zIndex: 1 }}>
        {isIosSafeMode ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#e9f1eb",
              color: "#1f2937",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Modo seguro iOS ativo
          </div>
        ) : (
          <>
            <MapView isShareMode={false} />
            <AccommodationPartnerOverlay />
          </>
        )}
      </div>

      {/* LOADER INICIAL */}
      {initialLoaderVisible && (
        <IntroScreen
          appReady={authReady}
          onClose={() => {
            setInitialLoaderVisible(false);
            if (!user && currentPanel?.name === "intro") {
              openPanel("login", { replaceStack: true });
            }
          }}
        />
      )}

      {/* PAINÉIS */}
      {ResolvedPanel && (
        <div
          className={
            ["bikeService", "bikeRental"].includes(currentPanel?.name)
              ? "tk-direct-modal-backdrop"
              : undefined
          }
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 900,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            pointerEvents: "none",
            background: ["bikeService", "bikeRental"].includes(currentPanel?.name)
              ? "rgba(8, 18, 13, 0.58)"
              : "transparent",
          }}
        >
          <div style={{ pointerEvents: "auto", width: "100%" }}>
            {["login", "bikeService", "bikeRental"].includes(currentPanel?.name) ? (
              <ResolvedPanel {...resolvedProps} />
            ) : (
              <PanelShell>
                {!user && currentPanel?.name !== "login" ? (
                  <PanelLogin />
                ) : (
                  <ResolvedPanel {...resolvedProps} />
                )}
              </PanelShell>
            )}
          </div>
        </div>
      )}

      {/* UI NORMAL */}
      <>
        <TopControlBar />
        {!isFullscreen && !isCoverPanel && <FloatingButtonBar />}
      </>

      {showSocialProfilePrompt && (
        <div className="tk-social-profile-backdrop" role="dialog" aria-modal="true" aria-labelledby="social-profile-title">
          <form className="tk-social-profile-modal" onSubmit={saveSocialProfileName}>
            <div className="tk-social-profile-icon" aria-hidden="true">
              <img src={`${import.meta.env.BASE_URL || "/"}icons/user-green.svg`} alt="" />
            </div>
            <div>
              <h3 id="social-profile-title">Completa o teu perfil</h3>
              <p>Queremos tratar-te pelo teu nome na comunidade Trekkin.</p>
            </div>
            <label htmlFor="social-profile-name">Nome completo</label>
            <input
              id="social-profile-name"
              type="text"
              autoComplete="name"
              autoFocus
              maxLength={100}
              value={socialProfileName}
              onChange={(event) => {
                setSocialProfileName(event.target.value);
                if (socialProfileError) setSocialProfileError("");
              }}
              placeholder="O teu nome"
            />
            {socialProfileError && <div className="tk-social-profile-error">{socialProfileError}</div>}
            <button type="submit" disabled={socialProfileSaving}>
              <img src={`${import.meta.env.BASE_URL || "/"}icons/check.svg`} alt="" />
              <span>{socialProfileSaving ? "A guardar..." : "Guardar e continuar"}</span>
              <b aria-hidden="true">›</b>
            </button>
          </form>
        </div>
      )}

      {showPostalPrompt && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(8, 15, 10, 0.45)",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              width: "min(560px, 100%)",
              background: "#ffffff",
              borderRadius: 16,
              border: "1px solid #d6e6d8",
              boxShadow: "0 24px 60px rgba(17, 54, 31, 0.22)",
              padding: 18,
              pointerEvents: "auto",
            }}
          >
            <h3 style={{ margin: "0 0 10px 0", fontSize: 24, color: "#1a5f35" }}>
              Ajuda-nos a melhorar a Trekkin na tua região
            </h3>
            <p style={{ margin: "0 0 8px 0", color: "#2e3b33", lineHeight: 1.45 }}>
              Indica o teu código postal (apenas uma vez).
            </p>
            <p
              style={{
                margin: "0 0 14px 0",
                color: "#59675d",
                fontSize: 14,
                lineHeight: 1.35,
              }}
            >
              Usamos este dado só para estatísticas geográficas (distrito), sem partilhar a tua
              localização exata.
            </p>

            <input
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              placeholder="Ex: 4700-000"
              value={postalCode}
              onChange={(e) => {
                const next = normalizePostalCode(e.target.value);
                setPostalCode(next || e.target.value);
                if (postalPromptError) setPostalPromptError("");
              }}
              style={{
                width: "100%",
                borderRadius: 12,
                border: "1px solid #cfded1",
                padding: "12px 14px",
                fontSize: 16,
                marginBottom: 10,
                boxSizing: "border-box",
              }}
            />

            {postalPromptError && (
              <div style={{ color: "#b42318", fontSize: 13, marginBottom: 10 }}>
                {postalPromptError}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                onClick={() => savePostalPrompt("provided")}
                disabled={postalPromptSaving}
                style={{
                  border: 0,
                  borderRadius: 10,
                  background: "#1f8f48",
                  color: "#fff",
                  padding: "10px 14px",
                  fontWeight: 700,
                  cursor: postalPromptSaving ? "not-allowed" : "pointer",
                  opacity: postalPromptSaving ? 0.7 : 1,
                }}
              >
                Guardar
              </button>
              <button
                onClick={() => savePostalPrompt("later")}
                disabled={postalPromptSaving}
                style={{
                  border: "1px solid #c8d8ca",
                  borderRadius: 10,
                  background: "#f6faf7",
                  color: "#254734",
                  padding: "10px 14px",
                  fontWeight: 600,
                  cursor: postalPromptSaving ? "not-allowed" : "pointer",
                }}
              >
                Agora não
              </button>
              <button
                onClick={() => savePostalPrompt("declined")}
                disabled={postalPromptSaving}
                style={{
                  border: "1px solid #c8d8ca",
                  borderRadius: 10,
                  background: "#fff",
                  color: "#4a5a4f",
                  padding: "10px 14px",
                  fontWeight: 600,
                  cursor: postalPromptSaving ? "not-allowed" : "pointer",
                }}
              >
                Não pretendo responder
              </button>
            </div>
          </div>
        </div>
      )}

      <LocationDisabledNotice />

      {processingOverlay && (
        <LoadingDotsOverlay
          text={processingOverlay.text}
          zIndex={10000}
          background="rgba(0,0,0,0.12)"
        />
      )}
    </div>
  );
}
